"use client";

import Link from "next/link";
import { Button } from "@/components/ui/button";
import { CheckCircle2, ArrowRight } from "lucide-react";
import Navbar from "./components/Navbar";
import HeroSection from "./components/HeroSection";
import StepsSection from "./components/StepsSection";
import FeaturesSection from "./components/FeaturesSection";
import SphereCarousel from "./components/SphereCarousel";
import FeaturesBentoSection from "./components/FeaturesBentoSection";

const FEATURE_PAGE_LINKS = [
  {
    href: "/ai-bedtime-stories",
    title: "AI Bedtime Stories",
    description:
      "Create personalized bedtime stories with narration, pictures, and age-appropriate themes.",
  },
  {
    href: "/story-book-creator",
    title: "Story Book Creator",
    description:
      "Build story books with pictures, printable pages, and classroom-ready reading materials.",
  },
  {
    href: "/personalized-story-books",
    title: "Personalized Story Books",
    description:
      "Make custom stories with names, favorite themes, and family-friendly narration.",
  },
  {
    href: "/audio-stories",
    title: "Audio Stories",
    description:
      "Turn stories into narrated listening experiences for bedtime, quiet time, and read-alongs.",
  },
  {
    href: "/choose-your-adventure-stories",
    title: "Choose Your Adventure Stories",
    description:
      "Build branching adventures and replayable story paths for kids and classrooms.",
  },
] as const;

const FAQ_ITEMS = [
  {
    question: "How does AI story generation work?",
    answer:
      "Choose a story format  Short Story, Anime, Drama, Poem, or Netflix-style Season. Then pick a genre, build your premise and characters through guided steps, and hit Generate. Storly's AI writes your complete, ready-to-read story in seconds.",
  },
  {
    question: "What types of stories can I create?",
    answer:
      "Storly supports five creative formats: Short Stories (Fantasy, Mystery, Dark Fantasy, Political Drama and more), Anime Narratives, Drama Series, Poems, and Netflix-style multi-episode Seasons. Each format has its own dedicated step-by-step builder.",
  },
  {
    question: "Can I customize characters and settings?",
    answer:
      "Yes. The Character Studio lets you create reusable characters  assign roles (hero, villain, mentor, antihero), write descriptions, and upload custom images. You also define your world setting, premise, and plot arcs before generating.",
  },
  {
    question: "Can I share my stories?",
    answer:
      "Absolutely. You can publish stories publicly to the Storly community, enter them in community contests, or keep them private. All stories are fully readable in-app with audio narration included on paid plans.",
  },
];

const BOOK_SHOWCASE_ITEMS: { id: string; alt: string }[] = [
  { id: "book01", alt: "Custom children's book about magical forests" },
  { id: "book02", alt: "Children's mystery story created with Storly" },
  { id: "book03", alt: "Science fiction story about space exploration" },
  { id: "book04", alt: "Illustrated bedtime story with AI narration" },
  { id: "book05", alt: "Animal adventure story generated by AI" },
  { id: "book06", alt: "Story about friendship and courage" },
  { id: "book07", alt: "AI-created fairy tale for young readers" },
  { id: "book08", alt: "Underwater adventure and sea creatures story" },
  { id: "book09", alt: "Superhero story for ages 5-8" },
  { id: "book10", alt: "Fantasy adventure about magical kingdoms" },
  { id: "book11", alt: "Dinosaur adventure for curious kids" },
  { id: "book12", alt: "Time travel and historical adventures" },
  { id: "book13", alt: "Nature and environmental conservation story" },
  { id: "book14", alt: "Story about robots and technology" },
  { id: "book15", alt: "Bedtime story with audio narration" },
  { id: "book16", alt: "Castles, knights and medieval adventures" },
  { id: "book18", alt: "Story about dreams and imagination" },
  { id: "book19", alt: "Adventure story with multiple paths" },
  { id: "book20", alt: "Pirate adventure for young readers" },
  { id: "book21", alt: "Magical garden and enchanted flower tale" },
  { id: "book22", alt: "Story about weather and seasons" },
  { id: "book23", alt: "Story about family values and togetherness" },
  { id: "book24", alt: "Children's narrative about sports and teamwork" },
  { id: "book25", alt: "Story about pets and animal care" },
  { id: "book26", alt: "Mystery adventure with clues and puzzles" },
  { id: "book27", alt: "Story about music, dance and artistic expression" },
  { id: "book28", alt: "Circus adventures and performers tale" },
  { id: "book29", alt: "Birthday party celebration story" },
  { id: "book07", alt: "Enchanted fairy tale for young readers" },
  { id: "book03", alt: "Galactic journey story for kids" },
  { id: "book10", alt: "Kingdom quest adventure story" },
  { id: "book05", alt: "Forest creature adventure story" },
  { id: "book19", alt: "Branching path adventure for kids" },
  { id: "book01", alt: "Mythical creatures story for children" },
  { id: "book14", alt: "Futuristic robot story for young minds" },
  { id: "book22", alt: "Seasonal nature story for children" },
  { id: "book06", alt: "Friendship adventure for young readers" },
  { id: "book12", alt: "Historical time-travel adventure" },
  { id: "book08", alt: "Ocean exploration story for kids" },
  { id: "book25", alt: "Animal care and friendship story" },
  { id: "book16", alt: "Medieval quest and adventure tale" },
  { id: "book27", alt: "Musical journey story for children" },
  { id: "book11", alt: "Prehistoric adventure for curious kids" },
  { id: "book20", alt: "High seas adventure story" },
  { id: "book04", alt: "Dreamy bedtime story with narration" },
  { id: "book09", alt: "Superhero origin story for kids" },
  { id: "book23", alt: "Heartwarming family adventure story" },
  { id: "book18", alt: "Imaginative dreamland story" },
  { id: "book26", alt: "Detective mystery story for kids" },
  { id: "book02", alt: "Mystery-solving adventure for children" },
  { id: "book13", alt: "Green Earth conservation story" },
];

export default function HomePage() {
  // Structured data for search engines and answer engines
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://storly.world/#organization",
        name: "Storly",
        url: "https://storly.world",
        logo: {
          "@type": "ImageObject",
          url: "https://storly.world/Storly-logo.png",
        },
        sameAs: [
          "https://x.com/storly",
          "https://facebook.com/storly",
          "https://instagram.com/storlyapp",
        ],
      },
      {
        "@type": "WebSite",
        "@id": "https://storly.world/#website",
        url: "https://storly.world",
        name: "Storly",
        description:
          "AI-powered creative writing platform for stories, anime, dramas, poems, and serialized seasons.",
        publisher: {
          "@id": "https://storly.world/#organization",
        },
        inLanguage: "en-US",
      },
      {
        "@type": "SoftwareApplication",
        "@id": "https://storly.world/#software",
        name: "Storly",
        applicationCategory: "EntertainmentApplication",
        operatingSystem: "Web",
        description:
          "AI-powered creative writing platform. Generate short stories, anime narratives, drama series, poems, and Netflix-style seasons across 10+ genres in seconds.",
        url: "https://storly.world",
        image: "https://storly.world/og-image.png",
        screenshot: "https://storly.world/og-image.png",
        creator: {
          "@id": "https://storly.world/#organization",
        },
        publisher: {
          "@id": "https://storly.world/#organization",
        },
        audience: {
          "@type": "Audience",
          audienceType:
            "writers, storytellers, teens, and creative fiction enthusiasts",
        },
        featureList: [
          "Instant AI story generation",
          "5 story formats: Short Stories, Anime, Dramas, Poems, Seasons",
          "10+ genres including Fantasy, Mystery, Dark Fantasy, Political Drama",
          "Character Studio with reusable characters",
          "Community publishing and contests",
          "Audio narration",
        ],
        offers: {
          "@type": "AggregateOffer",
          priceCurrency: "USD",
          lowPrice: "0",
          highPrice: "79",
          offerCount: "3",
        },
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "500",
        },
      },
      {
        "@type": "FAQPage",
        "@id": "https://storly.world/#faq",
        mainEntity: FAQ_ITEMS.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: item.answer,
          },
        })),
      },
      {
        "@type": "ItemList",
        "@id": "https://storly.world/#feature-pages",
        name: "Storly feature landing pages",
        itemListElement: FEATURE_PAGE_LINKS.map((link, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: link.title,
          url: `https://storly.world${link.href}`,
        })),
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      {/* JSON-LD Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      <Navbar />

      <main>
        {/* Hero Section with Professional Illustrations */}
        <HeroSection />

        {/* How It Works */}
        <StepsSection />

        {/* What Can You Build — Sticky Spiral Steps */}
        <FeaturesSection />

        {/* Who It's For */}
        {/* <section className="bg-gradient-to-br from-transparent via-blue-50 to-purple-50 relative py-16 md:py-20">
          <div className="absolute top-0 left-0 right-0 h-36 bg-gradient-to-b from-blue-200 to-transparent"></div>
          <div className="container mx-auto px-4">
            <div className="mb-12 text-center md:mb-16">
              <div className="flex items-center justify-center gap-4 mb-4">
                <div className="w-12 h-px bg-gray-900"></div>
                <span className="text-xs tracking-[0.3em] uppercase font-bold text-gray-900">
                  Who It&apos;s For
                </span>
                <div className="w-12 h-px bg-gray-900"></div>
              </div>
              <h2 className="mb-4 text-3xl font-black leading-tight text-gray-900 sm:text-4xl md:text-6xl">
                Built for Every
                <br />
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Kind of Creator
                </span>
              </h2>
              <p className="mx-auto max-w-2xl text-base font-light text-gray-600 sm:text-lg md:text-xl">
                From a teen building an anime series to a writer crafting a full Netflix-style season 
                Storly works for every creative ambition.
              </p>
            </div>

            <div className="mx-auto grid max-w-7xl gap-6 sm:gap-8 md:grid-cols-2 lg:grid-cols-4">
              {[
                {
                  title: "Teen Creators",
                  description:
                    "Build anime arcs, drama series, and fantasy worlds. The stories you actually want to write  finally within reach.",
                  emoji: "🌸",
                  gradient: "from-pink-500 to-rose-500",
                  image: "https://img.freepik.com/free-psd/close-up-kids-expression-portrait_23-2150193477.jpg?uid=R241192301&ga=GA1.1.841601622.1769160986&semt=ais_hybrid&w=740&q=80",
                },
                {
                  title: "Writers & Storytellers",
                  description:
                    "Draft premises, build characters, generate full seasons, and publish to an audience  all in one place.",
                  emoji: "✍️",
                  gradient: "from-orange-500 to-amber-500",
                  image: "https://img.freepik.com/premium-photo/tired-young-mother-sitting-floor-working-with-laptop-documents-while-little-kids-jumping-sofa-having-fun-making-noise_1212-12913.jpg?uid=R241192301&ga=GA1.1.841601622.1769160986&semt=ais_hybrid&w=740&q=80",
                },
                {
                  title: "Community Readers",
                  description:
                    "Discover stories written by creators across every genre. Read, follow your favorites, and join storytelling contests.",
                  emoji: "📖",
                  gradient: "from-blue-500 to-purple-500",
                  image: "https://images.unsplash.com/photo-1475503572774-15a45e5d60b9?w=800&q=80",
                },
                {
                  title: "Aspiring Screenwriters",
                  description:
                    "Build serialized seasons with episode arcs, recurring cast, and cliffhangers  like running your own show in minutes.",
                  emoji: "🎬",
                  gradient: "from-green-500 to-teal-500",
                  image: "https://img.freepik.com/premium-photo/photo-elementary-school-students-teaching-learning-activities_889227-69703.jpg?uid=R241192301&ga=GA1.1.841601622.1769160986&semt=ais_hybrid&w=740&q=80",
                },
              ].map((user, index) => (
                <div key={index} className="group h-full min-h-[320px]">
                  <div className="relative overflow-hidden rounded-3xl h-full flex flex-col transition-all duration-500 hover:scale-105 hover:-translate-y-3">
                    <div className="absolute inset-0 z-0">
                      <Image
                        src={user.image}
                        alt={user.title}
                        fill
                        unoptimized
                        quality={90}
                        sizes="(min-width: 1024px) 24vw, (min-width: 768px) 42vw, 92vw"
                        className="object-cover object-top transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className={`absolute inset-0 bg-gradient-to-br ${user.gradient} opacity-70`} />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    </div>
                    <div className="relative z-10 h-full flex flex-col p-6 sm:p-8">
                      <div className="mt-auto space-y-2">
                        <div className="text-3xl">{user.emoji}</div>
                        <h3 className="text-2xl font-black text-white drop-shadow-lg sm:text-3xl">
                          {user.title}
                        </h3>
                        <p className="text-sm text-white/90 leading-relaxed">
                          {user.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section> */}

        {/* Story Showcase — Sphere Carousel */}
        <section className="bg-gradient-to-br from-green-50 via-green-50 to-white py-16 md:py-20">
          <div className="container mx-auto px-4">
            <div className="mb-6 text-center md:mb-10">
              <div className="flex items-center justify-center gap-4 mb-4">
                <div className="w-12 h-px bg-gray-900" />
                <span className="text-xs tracking-[0.3em] uppercase font-bold text-gray-900">
                  Story Examples
                </span>
                <div className="w-12 h-px bg-gray-900" />
              </div>
              <h2 className="mb-4 text-3xl font-black leading-tight text-gray-900 sm:text-4xl md:text-6xl">
                See the Magic
                <br />
                <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
                  In Action
                </span>
              </h2>
              <p className="mx-auto max-w-2xl text-base font-light text-gray-600 sm:text-lg md:text-xl">
                Browse examples of fantasy stories, anime narratives, political
                dramas, mystery thrillers, and serialized seasons created with
                Storly.
              </p>
            </div>
          </div>

          <SphereCarousel books={BOOK_SHOWCASE_ITEMS} />
        </section>

        {/* Everything You Need — Bento Grid */}
        <FeaturesBentoSection />
 

        {/* Pricing Section - Magazine Style */}
        <section className="relative bg-white py-16 md:py-20" id="pricing">
          <div className="absolute top-0 left-0 right-0 h-30 bg-gradient-to-b from-purple-200 to-transparent"></div>
          <div className="container mx-auto px-4">
            {/* Section Header */}
            <div className="mb-12 text-center md:mb-16">
              <div className="flex items-center justify-center gap-4 mb-4">
                <div className="w-12 h-px bg-gray-900"></div>
                <span className="text-xs tracking-[0.3em] uppercase font-bold text-gray-900">
                  Pricing Plans
                </span>
                <div className="w-12 h-px bg-gray-900"></div>
              </div>
              <h2 className="mb-4 text-3xl font-black leading-tight text-gray-900 sm:text-4xl md:text-6xl">
                Simple,
                <br />
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Transparent Pricing
                </span>
              </h2>
              <p className="mx-auto max-w-2xl text-base font-light text-gray-600 sm:text-lg md:text-xl">
                Choose the plan that fits your creative output. Upgrade or downgrade anytime.
              </p>
            </div>

            {/* Magazine-Style Pricing Cards */}
            <div className="mx-auto grid max-w-7xl gap-5 sm:gap-6 md:grid-cols-3 lg:gap-6">
              {pricingPlans.map((plan, index) => {
                const accentBar = [
                  "from-blue-400 to-indigo-500",
                  "from-purple-600 via-pink-600 to-blue-600",
                  "from-yellow-500 to-orange-500",
                ][index];
                const nameGrad = [
                  "from-blue-500 to-indigo-600",
                  "from-purple-600 to-pink-600",
                  "from-yellow-500 to-orange-600",
                ][index];
                const checkColor = [
                  "text-blue-500",
                  "text-purple-600",
                  "text-amber-500",
                ][index];
                const btnClass = [
                  "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white",
                  "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white",
                  "bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white",
                ][index];

                return (
                <div
                  key={index}
                  className={`relative border-2 rounded-2xl overflow-hidden transition-all hover:-translate-y-2 ${
                    plan.popular
                      ? "z-10 border-purple-300 bg-gradient-to-br from-purple-50 to-pink-50 shadow-2xl xl:scale-105"
                      : "border-gray-200 shadow-lg bg-white hover:border-blue-300 hover:shadow-xl"
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-px left-0 right-0 flex justify-center">
                      <span className="rounded-b-full bg-gradient-to-r from-purple-600 to-pink-600 px-4 py-1 text-xs font-black text-white shadow">
                        Most Popular
                      </span>
                    </div>
                  )}

                  {/* Top Accent Bar */}
                  <div className={`h-2 bg-gradient-to-r ${accentBar}`}></div>

                  <div className="space-y-5 p-5 sm:space-y-6 sm:p-6">
                    {/* Plan Name & Price */}
                    <div className="text-center pt-4">
                      <h3 className={`mb-4 text-xl font-black sm:mb-5 sm:text-2xl bg-gradient-to-r bg-clip-text text-transparent ${nameGrad}`}>
                        {plan.name}
                      </h3>
                      <div className="mb-4">
                        <div className="flex items-baseline justify-center gap-2">
                          <span className={`bg-gradient-to-r bg-clip-text text-4xl font-black text-transparent sm:text-5xl ${nameGrad}`}>
                            ${plan.price}
                          </span>
                          <span className="text-lg font-bold text-gray-600">/month</span>
                        </div>
                      </div>
                      <p className="flex min-h-[48px] items-center justify-center text-sm font-medium text-gray-600">
                        {plan.description}
                      </p>
                    </div>

                    {/* Divider */}
                    <div className="w-full h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>

                    {/* Features */}
                    <ul className="space-y-3">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <CheckCircle2 className={`w-5 h-5 shrink-0 mt-0.5 ${checkColor}`} />
                          <span className="text-sm font-medium leading-tight text-gray-700">
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>

                    {/* CTA Button */}
                    <a
                      target="_blank"
                      href={`https://app.storly.world/onboarding/pricing?plan=${plan.name.toLowerCase()}`}
                      rel="noreferrer"
                    >
                      <Button className={`w-full py-6 text-base font-bold rounded-xl shadow-md hover:shadow-lg transition-all border-0 ${btnClass}`}>
                        <ArrowRight className="w-5 h-5 mr-2 inline" />
                        Get {plan.name}
                      </Button>
                    </a>
                  </div>
                </div>
                );
              })}
            </div>

            {/* Free plan subtle link */}
            <p className="mt-8 text-center text-sm text-gray-500">
              Just want to try it first?{" "}
              <a
                href="https://app.storly.world/register"
                target="_blank"
                rel="noreferrer"
                className="font-semibold text-gray-700 underline underline-offset-2 hover:text-purple-600 transition-colors"
              >
                Start free  no credit card required
              </a>
            </p>
          </div>
        </section>
        {/* FAQ Section - Clean Magazine Style */}
        <section className="bg-white py-16 relative md:py-20" id="faq">

          <div className="absolute top-0 left-0 right-0 h-30 bg-gradient-to-b from-indigo-200 to-transparent"></div>
          <div className="container mx-auto px-4">
            {/* Section Header */}
            <div className="mb-12 text-center md:mb-16">
              <div className="flex items-center justify-center gap-4 mb-4">
                <div className="w-12 h-px bg-gray-900"></div>
                <span className="text-xs tracking-[0.3em] uppercase font-bold text-gray-900">
                  FAQ
                </span>
                <div className="w-12 h-px bg-gray-900"></div>
              </div>
              <h2 className="mb-4 text-3xl font-black leading-tight text-gray-900 sm:text-4xl md:text-6xl">
                Questions?
                <br />
                <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  We&apos;ve Got Answers
                </span>
              </h2>
            </div>

            {/* FAQ Grid */}
            <div className="mx-auto max-w-4xl space-y-4 sm:space-y-6">
              {FAQ_ITEMS.map((faq, index) => (
                <div
                  key={index}
                  className="rounded-2xl border-2 border-gray-200 bg-gradient-to-br from-gray-50 to-white p-5 transition-all hover:border-purple-300 sm:p-8"
                >
                  <h3 className="mb-3 text-lg font-black text-gray-900 sm:text-xl">
                    {faq.question}
                  </h3>
                  <p className="text-sm leading-relaxed text-gray-600 sm:text-base">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>

            {/* CTA at bottom of FAQ */}
            <div className="text-center mt-16">
              <p className="mb-6 text-base text-gray-600 sm:text-lg">
                Have questions or need help?
              </p>
              <div className="flex items-center justify-center gap-4 flex-wrap">
                <Link href="/support">
                  <Button className="bg-gradient-to-r from-blue-600 to-purple-600 px-7 py-5 text-base font-semibold text-white rounded-xl shadow-lg transition-all border-0 hover:from-blue-700 hover:to-purple-700 hover:shadow-xl sm:px-10 sm:py-6 sm:text-lg">
                    Contact Support
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

      </main>

    </div>
  );
}

const pricingPlans = [
  {
    name: "Starter",
    price: 20,
    description:
      "For writers who want to create regularly and unlock the full story toolkit.",
    features: [
      "20 stories per month",
      "All story types unlocked",
      "Audio narration",
      "Draw your own characters",
      "10 builder templates",
      "Save & share stories",
      "Competition access",
    ],
    popular: false,
  },
  {
    name: "Pro",
    price: 49,
    description:
      "For active storytellers who create regularly and want the full platform experience.",
    features: [
      "Everything in Starter",
      "30 public stories",
      "All templates unlocked",
      "Competitions & prizes",
      "Awards & certificates",
      "Priority support",
      "Community recognition",
    ],
    popular: true,
  },
  {
    name: "Elite",
    price: 79,
    description:
      "For serious storytellers who want expert guidance and dedicated 1-on-1 support.",
    features: [
      "Everything in Pro",
      "Unlimited public stories",
      "Expert story guidance",
      "Competition assistance",
      "Dedicated 1-on-1 support",
      "Fastest priority response",
    ],
    popular: false,
  },
];
